import type { ReportSectorObservation } from './types';
import { SECTOR_SUM_CODES } from './sectorMapping';

export const GROUP_CODES = {
  onRoad: ['ldv', 'hdv'] as const,
  buildings: ['elec_res', 'elec_com', 'ng_res', 'ng_com'] as const,
  waste: ['waste'] as const,
  offroad: ['offroad'] as const,
  agriculture: ['agriculture'] as const,
};

export const GROUP_COLORS: Record<string, string> = {
  on_road: '#0f766e',
  buildings: '#2563eb',
  waste: '#a16207',
  offroad: '#334155',
  agriculture: '#4d7c0f',
};

export const DETAILED_SECTOR_COLORS: Record<string, string> = {
  ldv: '#0f766e',
  hdv: '#115e59',
  elec_res: '#2563eb',
  elec_com: '#7c3aed',
  ng_res: '#c2410c',
  ng_com: '#9a3412',
  waste: '#a16207',
  offroad: '#334155',
  agriculture: '#4d7c0f',
};

export const CBEI_CATEGORY_COLORS = ['#0f766e', '#2563eb', '#c2410c', '#7c3aed', '#a16207'] as const;

export interface GroupedSectorBar {
  groupId: string;
  label: string;
  emissionsMt: number;
  share: number;
}

function sectorValue(sectors: ReportSectorObservation[], code: string): number | null {
  const s = sectors.find((x) => x.sectorCode === code);
  if (!s || s.emissionsMt.value == null) return null;
  if (s.emissionsMt.status === 'missing' || s.emissionsMt.status === 'not_modeled') return null;
  return s.emissionsMt.value;
}

/** Live agriculture zeros are treated as calculation-stack placeholders, not charted sources. */
export function isAgriculturePlaceholderZero(sectors: ReportSectorObservation[]): boolean {
  const ag = sectors.find((s) => s.sectorCode === 'agriculture');
  if (!ag) return true;
  if (ag.emissionsMt.value == null) return true;
  return ag.emissionsMt.value === 0;
}

export function shouldChartAgriculture(sectors: ReportSectorObservation[]): boolean {
  const v = sectorValue(sectors, 'agriculture');
  return v != null && v > 0;
}

function sumCodes(sectors: ReportSectorObservation[], codes: readonly string[]): number | null {
  let sum = 0;
  let any = false;
  for (const code of codes) {
    const v = sectorValue(sectors, code);
    if (v == null) continue;
    sum += v;
    any = true;
  }
  return any ? sum : null;
}

/**
 * Presentation grouping of already-calculated sector values.
 * Does not apply emission factors or activity formulas.
 */
export function groupedSectorBars(sectors: ReportSectorObservation[], totalMt: number | null): GroupedSectorBar[] {
  const denom = totalMt != null && totalMt > 0 ? totalMt : null;
  const rows: Array<{ groupId: string; label: string; emissionsMt: number | null }> = [
    { groupId: 'on_road', label: 'On-road transportation', emissionsMt: sumCodes(sectors, GROUP_CODES.onRoad) },
    { groupId: 'buildings', label: 'Building energy', emissionsMt: sumCodes(sectors, GROUP_CODES.buildings) },
    { groupId: 'waste', label: 'Waste & wastewater', emissionsMt: sumCodes(sectors, GROUP_CODES.waste) },
    { groupId: 'offroad', label: 'Off-road', emissionsMt: sumCodes(sectors, GROUP_CODES.offroad) },
  ];
  if (shouldChartAgriculture(sectors)) {
    rows.push({
      groupId: 'agriculture',
      label: 'Agriculture',
      emissionsMt: sumCodes(sectors, GROUP_CODES.agriculture),
    });
  }
  return rows
    .filter((r): r is { groupId: string; label: string; emissionsMt: number } => r.emissionsMt != null)
    .map((r) => ({
      ...r,
      share: denom != null ? r.emissionsMt / denom : 0,
    }));
}

export function detailedChartSectors(sectors: ReportSectorObservation[]): ReportSectorObservation[] {
  return sectors.filter((s) => {
    if (!SECTOR_SUM_CODES.includes(s.sectorCode as (typeof SECTOR_SUM_CODES)[number])) return false;
    if (s.sectorCode === 'agriculture' && isAgriculturePlaceholderZero(sectors)) return false;
    return s.emissionsMt.value != null && s.emissionsMt.status !== 'missing' && s.emissionsMt.status !== 'not_modeled';
  });
}

export function sectorShare(emissions: number | null, totalMt: number | null): number | null {
  if (emissions == null || totalMt == null || totalMt <= 0) return null;
  return emissions / totalMt;
}

export function transportBuildingShares(
  sectors: ReportSectorObservation[],
  totalMt: number | null
): { transportShare: number | null; buildingShare: number | null; transportMt: number | null; buildingMt: number | null } {
  const transportMt = sumCodes(sectors, GROUP_CODES.onRoad);
  const buildingMt = sumCodes(sectors, GROUP_CODES.buildings);
  return {
    transportMt,
    buildingMt,
    transportShare: sectorShare(transportMt, totalMt),
    buildingShare: sectorShare(buildingMt, totalMt),
  };
}

export const UNUSUAL_DENOMINATOR_POPULATION_MAX = 1000;

export function hasUnusualResidentDenominator(population: number | null | undefined): boolean {
  return population != null && Number.isFinite(population) && population > 0 && population < UNUSUAL_DENOMINATOR_POPULATION_MAX;
}

export function benchmarkYears(
  years: number[],
  inventoryYear: number,
  preferred = [2005, 2019]
): number[] {
  const set = new Set(years);
  const out: number[] = [];
  for (const y of preferred) {
    if (set.has(y)) out.push(y);
  }
  if (set.has(inventoryYear) && !out.includes(inventoryYear)) out.push(inventoryYear);
  return out;
}
